import { ActionIcon, Flex, Modal, Tooltip } from "@mantine/core";
import { IAzureBlobFile } from "@schemas/azureSchema";
import { IconDownload, IconExternalLink, IconTrash } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React from "react";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { formatShortDate } from "../../app/routes/admin/selectors";
import { Checkbox, ConfirmDialog } from ".././";

const FileTable: React.FC<{
  files: IAzureBlobFile[];
  downloadFile: (fileId: string, fileName: string) => void;
  deleteFile: (fileId: string) => void;
  showDocxImageTag?: boolean;
  folderPath?: string;
  allowToggleItems?: boolean;
  onToggleItem?: (fileId: string) => void;
}> = ({
  files,
  downloadFile,
  deleteFile,
  showDocxImageTag = false,
  folderPath,
  allowToggleItems = false,
  onToggleItem,
}) => {
  const [openImageModal, setOpenImageModal] = React.useState(false);
  const [activeImg, setActiveImg] = React.useState("");

  const onClickImg = (filename: string) => {
    setActiveImg(filename);
    setOpenImageModal(true);
  };

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  }

  const columns: ColDef<IAzureBlobFile>[] = [
    {
      headerName: "ID",
      field: "blobName",
      hide: true,
    },
    {
      headerName: "",
      suppressHeaderMenuButton: true,
      sortable: false,
      width: 80,
      cellClass: "px-1",
      cellRenderer: (params) => {
        return (
          <div className="d-flex">
            <Tooltip label="Last ned" withArrow position="top">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="sm"
                onClick={() => downloadFile(params.data?.blobName, params.data?.fileName)}
                aria-label="Last ned fil"
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
            <ConfirmDialog
              title="Slette fil?"
              message="Er du sikker på at du vil slette filen?"
              confirmCb={() => deleteFile(params.data?.blobName)}
            >
              <Tooltip label="Slett" withArrow position="top">
                <ActionIcon variant="subtle" color="red" size="sm" aria-label="Slett fil">
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </ConfirmDialog>
          </div>
        );
      },
    },
    {
      headerName: "Filnavn",
      field: "fileName",
      cellRenderer: (params) => {
        return <div className="ag-cell-text-truncate">{params.value}</div>;
      },
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: "Tagg for docxtemplater",
      field: showDocxImageTag === false ? "blobName" : "fileName", // MÅ ENDRES
      hide: showDocxImageTag === false,
      cellRenderer: (params) => (params.value != null ? `{%${params.value}}` : ""),
    },
    {
      headerName: "Opprettet",
      field: "uploadedAt",
      initialSort: "desc",
      cellRenderer: (params) => formatShortDate(params.value),
      width: 130,
    },
    {
      headerName: "Opplastet av",
      field: "uploadedBy",
    },
    {
      headerName: "Størrelse",
      field: "size",
      cellRenderer: (params) => {
        const formattedSize = formatFileSize(params.value);
        return <div className="ag-cell-text-truncate">{formattedSize}</div>;
      },
      width: 100,
    },
    {
      headerName: "Forhåndsvisning",
      sortable: false,
      cellClass: "d-flex justify-content-center",
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => {
        const isPdf = params.data?.fileName.toLowerCase().endsWith(".pdf");
        return (
          <Flex>
            {isPdf ? (
              <a
                style={{ textDecoration: "none", color: "var(--mantine-color-blue-4)" }}
                href={`/api/pdf/${params.data?.blobName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Flex gap="xs" align="center" justify="center" h="100%">
                  <IconExternalLink size={24} />
                </Flex>
              </a>
            ) : (
              <img
                className="pointer"
                src={`/api/image/${params.data?.blobName}`}
                alt=""
                style={{ maxWidth: "100px", maxHeight: "60px", objectFit: "contain" }}
                onClick={() => onClickImg(params.data?.blobName)}
              />
            )}
          </Flex>
        );
      },
    },
    {
      headerName: "",
      hide: allowToggleItems !== true,
      suppressHeaderMenuButton: true,
      cellRenderer: (params) => {
        return (
          <Checkbox
            isChecked={!!params}
            setChecked={() => {
              onToggleItem(params.data?._id);
            }}
            disabled={false}
          />
        );
      },
    },
  ];

  return (
    <div className="ag-theme-quartz h-100 w-100">
      <AgGridReact
        domLayout={"autoHeight"}
        rowData={files}
        defaultColDef={{
          suppressHeaderMenuButton: true,
        }}
        rowHeight={45}
        columnDefs={columns}
        suppressClickEdit={true}
        rowSelection={{ mode: "singleRow", checkboxes: false }}
        animateRows={true}
        suppressContextMenu={true}
        onGridReady={(e) => {
          e.api.autoSizeColumns(["createdBy.name"]);
        }}
        localeText={AG_GRID_LOCALE_NOR}
      />
      {openImageModal === true && (
        <Modal
          opened={openImageModal}
          onClose={() => setOpenImageModal(false)}
          title={files?.find((file) => file.blobName === activeImg)?.fileName}
          size="50%"
          styles={{ content: { maxWidth: "1000px" } }}
        >
          <div>
            <img src={`/api/image/${activeImg}`} alt="" width="100%" />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default FileTable;
